/**
 * TVSafeAreaLayout — P2.8-3b: TV Safe-Area Layout Component
 *
 * Wraps guest views with TV-safe margins and a maximum content width.
 * Activates only when the viewport width is ≥ 1280 px (TV mode, via useIsTV).
 *
 * Responsibilities:
 *  - Apply 8vh / 8vw internal padding to keep content within TV overscan bounds
 *  - Constrain content width to 1600 px and centre it horizontally
 *  - Expose a `data-tv-mode` attribute so CSS can apply TV-specific overrides
 *    to all descendant elements (typography, spacing, target sizes)
 *
 * Rules:
 *  - Does NOT modify desktop layout (renders passthrough when NOT in TV mode)
 *  - Uses viewport units only — no fixed pixels for safe-area padding
 *  - No user-agent detection — viewport-width only
 */

import type { ReactNode } from 'react';
import { useIsTV } from '@/hooks';

interface TVSafeAreaLayoutProps {
  children: ReactNode;
  /** Additional CSS classes forwarded to the outer wrapper div */
  className?: string;
}

export function TVSafeAreaLayout({ children, className = '' }: TVSafeAreaLayoutProps) {
  const isTV = useIsTV();

  if (!isTV) {
    // Desktop / tablet passthrough — preserve existing layout entirely.
    return (
      <div className={`h-full ${className}`}>
        {children}
      </div>
    );
  }

  // TV mode: overscan-safe padding + centred max-width constraint.
  return (
    <div
      className={`tv-safe-area-layout ${className}`}
      data-tv-mode="true"
    >
      <div className="tv-safe-area-inner">
        {children}
      </div>
    </div>
  );
}
